import { useState } from 'react';
import { Link } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { DocumentSelectorModal } from '@frontend/document-selector-modal';
import { useCombinedDocumentsListV3 } from '@frontend/documents-hooks';
import {
  DynamicFieldAttributes,
  useDynamicFieldInsertion,
  useEmptySpaceInsertion,
  useLinkInsertion,
  useModalControl,
} from '@frontend/design-system';
import { useContentComposer } from '../../content-composer-provider';
import { CustomLinkToolbar } from './custom-link-toolbar';
import { LinkOptionsToolbar } from './link-options-toolbar';
import { FloatingToolbarCenter } from '.';

type Props = {
  selectedDynamicField?: DynamicFieldAttributes;
  selectRTEToolbar: () => void;
};

export const RTEButtonsToolbar = ({ selectedDynamicField, selectRTEToolbar }: Props) => {
  const { form, locationIds, setForm } = useContentComposer();
  const [showCustom, setShowCustom] = useState(false);

  const insertDynamicField = useDynamicFieldInsertion();
  const insertEmptySpace = useEmptySpaceInsertion();
  const insertLink = useLinkInsertion();

  const handleDynamicFieldInsertion = (dynamicField: DynamicFieldAttributes) => {
    insertDynamicField(dynamicField, true);
    insertEmptySpace();
    setShowCustom(false);
    selectRTEToolbar();
  };

  const handleLinkInsertion = (link: Link, keyPress?: KeyboardEvent) => {
    if (!link.text) return;
    insertLink(link.url, link.text, keyPress);
    insertEmptySpace();
    setShowCustom(false);
    selectRTEToolbar();
  };

  const handleLinkClick = (link: Link) => {
    switch (link.linkType) {
      case 'form':
        // let the form flow open the document selector modal
        break;
      case 'custom':
        // let the user input a custom text and link
        break;
      case 'schedule':
        handleDynamicFieldInsertion({ label: 'Schedule Link', symbol: '{{SCHEDULE_LINK}}', val: 'SCHEDULE_LINK' });
        break;
      case 'phone':
        handleDynamicFieldInsertion({
          label: 'Practice Phone',
          symbol: '{{BUSINESS_GROUP_PHONE}}',
          val: 'BUSINESS_GROUP_PHONE',
        });
        break;
      case 'address':
        handleDynamicFieldInsertion({
          label: 'Practice Address',
          symbol: '{{BUSINESS_GROUP_ADDRESS}}',
          val: 'BUSINESS_GROUP_ADDRESS',
        });
        break;
      case 'confirmation':
        handleDynamicFieldInsertion({
          label: 'Confirmation Link',
          symbol: '{{CALL_TO_ACTION_LINK}}',
          val: 'CALL_TO_ACTION_LINK',
        });
        break;
      default:
    }
  };

  // FORM
  const documents = useCombinedDocumentsListV3(locationIds);
  const { modalProps, triggerProps } = useModalControl();
  const openDocumentSelectorModal = () => {
    triggerProps.onClick();
  };
  const updateDocumentId = (documentId: string) => {
    if (selectedDynamicField) handleDynamicFieldInsertion(selectedDynamicField);
    const documentType = documents?.find((form) => form.id === documentId)?.documentType ?? 'form';
    setForm?.({ documentId, documentType }); // set the formId at the consumer level
    modalProps.onClose();
  };

  return (
    <>
      <FloatingToolbarCenter orientation='bottom' css={{ left: '50%' }} honorX>
        {showCustom ? (
          <CustomLinkToolbar
            handleSubmit={handleLinkInsertion}
            setShow={setShowCustom}
            showLinkText={true}
            css={{
              width: 485,
            }}
          />
        ) : (
          <LinkOptionsToolbar
            onClick={handleLinkClick}
            openModal={openDocumentSelectorModal}
            setShowCustom={setShowCustom}
          />
        )}
      </FloatingToolbarCenter>
      <DocumentSelectorModal
        modalProps={modalProps}
        documents={documents}
        documentId={form?.documentId}
        updateDocumentId={updateDocumentId}
      />
    </>
  );
};
